<template>
  <PrismEditor
    :value="value"
    :highlight="highlighter"
    line-numbers
    class="ak-code-editor"
    :style="pixelHeight"
    @input="update"
  ></PrismEditor>
</template>

<script>
import { defineComponent, computed } from 'vue'
import { PrismEditor } from 'vue-prism-editor'

// import highlighting library (you can use any library you want just return html string)
import { highlight, languages } from 'prismjs/components/prism-core'
import 'prismjs/components/prism-clike'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-css'
import 'prismjs/components/prism-markup'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-close />
   */
  name: 'AkCodeEditor',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    PrismEditor,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    value: {
      type: String,
      default: null,
    },
    height: {
      type: Number,
      default: null,
    },
    language: {
      type: String,
      default: 'js',
      validator: (value) => {
        return ['js', 'css', 'html'].indexOf(value) >= 0
      },
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['input'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const pixelHeight = computed(() => {
      return {
        height: `${props.height}px`,
      }
    })

    const update = (newValue) => {
      emit('input', newValue)
    }

    const highlighter = (code) => {
      return highlight(code, languages[props.language])
    }

    return {
      pixelHeight,
      update,
      highlighter,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';

@import '~vue-prism-editor/dist/prismeditor.min.css'; // editor styles
@import '~prismjs/themes/prism-tomorrow.css'; // syntax highlighting styles
.ak-code-editor {
  min-height: 100%;
  background: $color-grey-dark;
  color: $color-grey-lighter;
  font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
  font-size: 14px;
  line-height: 1.5;
  padding-top: $spacing-small;
  padding-bottom: $spacing-small;
}

.prism-editor__container {
  min-height: 100%;
  .prism-editor__textarea {
    height: 100% !important;
  }
}

/* optional class for removing the outline */
.prism-editor__textarea:focus {
  outline: none;
}
</style>
